
import type {
    SessionExchangeResponse,
    JourneyData,
} from "../types/journey.types";
import { ApiError } from "./ApiError";
import { getAuthHeaders } from "./api";

const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || "https://stg-native-api.cleverpe.com/api";

export const exchangeSessionCode = async (
    code: string
): Promise<SessionExchangeResponse> => {

    const response = await fetch(
        `${API_BASE_URL}/journey/session/exchange`,
        {
            method: "POST",

            headers: {
                "Content-Type": "application/json",
            },

            body: JSON.stringify({
                code,
            }),
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new Error(
            data?.message ||
            "Unable to initialize journey."
        );
    }

    return data;
};

export const getJourney = async (): Promise<
    SessionExchangeResponse
> => {

    const response = await fetch(
        `${API_BASE_URL}/journey`,
        {
            method: "GET",

            headers: getAuthHeaders(),
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message ||
            "Unable to restore journey.",
            response.status,
            data
        );
    }

    return data;
};

/*
 * =========================================================
 * GET JOURNEY BY ORDER ID
 * =========================================================
 *
 * Used by the payment gateway return page.
 *
 * The customer may return from the gateway without the
 * previous browser JWT session, so this endpoint does NOT
 * send Authorization headers.
 *
 * GET /journey/order/:orderId
 */

export interface JourneyByOrderIdResponse {
    success: boolean;

    data: {
        orderId?: string;
        orderAmount?: number;
        dpAmount?: number;
        customerName?: string;
        productDetails?: string;
        emiAmount?: number;
        callbackUrl?: string;
        customerEmail?: string;
        firstEmiDueDate?: string;
    };
}

export const getJourneyByOrderId = async (
    orderId: string
): Promise<JourneyByOrderIdResponse> => {

    if (!orderId) {
        throw new ApiError(
            "Order ID is required.",
            400
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/journey/order/${encodeURIComponent(
            orderId
        )}`,
        {
            method: "GET",

            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Unable to fetch transaction details.",
            response.status,
            data
        );
    }

    return data;
};

/*
 * =========================================================
 * JOURNEY ACTION
 * =========================================================
 *
 * Currently implemented:
 *
 * CKYC
 *  ├── SEND_OTP
 *  └── VERIFY_OTP
 *
 * DigiLocker:
 *  └── Not implemented yet
 *
 * =========================================================
 */

export interface JourneyActionRequest {
    subStep:
    | "CKYC"
    | "DIGILOCKER"
    | "BANKING_DETAILS"
    | "E_MANDATE"
    | "AGREEMENT";

    action:
    | "SEND_OTP"
    | "VERIFY_OTP"
    | "GENERATE"
    | "CREATE";

    payload: Record<string, unknown>;

}

export interface JourneyActionResponse {
    success: boolean;

    data: {
        success: boolean;
        message: string;

        metadata?: {
            requestId?: string;
            link?: string;
            shareCode?: string;
            mandateLink?: string;
            mandateId?: string;
            customerIdentifier?: string;
            docId?: number;
            pdf?: string;
            eagreementLink?: string;
            agreementSignedCopy?: string | null;
        };

        journey: JourneyData;
    };
}

/*
 * =========================================================
 * JOURNEY ACTION API
 * =========================================================
 */

export const journeyAction = async (
    request: JourneyActionRequest
): Promise<JourneyActionResponse> => {

    const token =
        localStorage.getItem("journey_token");

    if (!token) {
        throw new ApiError(
            "Journey session expired.",
            401
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/journey/action`,
        {
            method: "POST",

            headers: getAuthHeaders(),

            body: JSON.stringify(request),
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Journey action failed.",
            response.status,
            data
        );
    }

    return data;
};

export interface JourneyStatusResponse {
    success: boolean;

    data: {
        success: boolean;
        message: string;

        metadata?: {
            status?: string;
            agreementStatus?: string;
        };

        journey: JourneyData;
    };
}

export const journeyStatus = async (
    step: string
): Promise<JourneyStatusResponse> => {
    const token =
        localStorage.getItem("journey_token");

    if (!token) {
        throw new ApiError(
            "Journey session expired.",
            401
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/journey/status`,
        {
            method: "POST",

            headers: getAuthHeaders(),

            body: JSON.stringify({
                step,
            }),
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Unable to fetch journey status.",
            response.status,
            data
        );
    }

    return data;
};

export interface CompleteDigiLockerResponse {
    success: boolean;

    data: {
        journey: JourneyData;
    };
}

export const completeDigiLocker = async (
    eAadhaar: string
): Promise<CompleteDigiLockerResponse> => {

    const token =
        localStorage.getItem("journey_token");

    if (!token) {
        throw new ApiError(
            "Journey session expired.",
            401
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/journey/digilocker/callback`,
        {
            method: "POST",

            headers: getAuthHeaders(),

            body: JSON.stringify({
                eAadhaar,
            }),
        }
    );

    const data =
        await response.json();

    if (!response.ok) {

        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Unable to complete DigiLocker verification.",
            response.status,
            data
        );

    }

    return data;
};


/*
 * =========================================================
 * UPDATE JOURNEY PHASE
 * =========================================================
 */


export const updateJourneyPhase = async (
    phase: string,
    orderAmount?: number
): Promise<{ success: boolean; data: JourneyData }> => {

    const token = localStorage.getItem("journey_token");

    if (!token) {
        throw new ApiError("Journey session expired.", 401);
    }

    const response = await fetch(
        `${API_BASE_URL}/journey/phase`,
        {
            method: "PATCH",
            headers: {
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                phase,
                ...(orderAmount !== undefined
                    ? { orderAmount }
                    : {}),
            }),
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message || data?.data?.message || "Unable to update journey phase.",
            response.status,
            data
        );
    }

    return data;
};

/*
 * =========================================================
 * UPDATE SCHEME
 * =========================================================
 */

export interface UpdateSchemeRequest {
    schemeId: string;
}

export interface UpdateSchemeResponse {
    success: boolean;
    data: JourneyData;
}

export const updateScheme = async (
    request: UpdateSchemeRequest
): Promise<UpdateSchemeResponse> => {

    const token =
        localStorage.getItem("journey_token");

    if (!token) {
        throw new ApiError(
            "Journey session expired.",
            401
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/partner/updatescheme`,
        {
            method: "PATCH",

            headers: {
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },

            body: JSON.stringify(request),
        }
    );

    const data =
        await response.json();

    if (!response.ok) {

        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Unable to update scheme.",
            response.status,
            data
        );

    }

    return data;
};
/*
 * =========================================================
 * GET CURRENT ORDER
 * =========================================================
 *
 * JWT contains journeyId.
 *
 * Backend:
 *   JWT → journeyId
 *       → Journey.orderId
 *       → Order
 *
 * Used by LoanSummary because Redux scheme state can be
 * lost after an external redirect/callback.
 * =========================================================
 */

export interface CurrentOrderResponse {
    success: boolean;

    data: {
        orderId: string;
        amount: number;
        lender?: string;
        category?: string;
        createdAt?: string;

        productDetails?: string;
        productCategory?: string;
        productBrand?: string;

        keyFactStatement?: {
            amount: number;

            scheme?: {
                _id: string;
                name: string;
                grossTenure: number;
                advancedEMI: number;
                subventionCost: number;
                interestBased: boolean;
                annualInterest: number;
                processingRate: number;
                GST: number;
                min: number;
                calOnRootAmt: boolean;
                for: string[];
                lender: string[];
                isActive: boolean;
                category: string[];
                schemeId: string;
            };

            calculations?: {
                loanAmount: number;
                grossTenure: number;
                advancedEMI: number;
                NoOfNetEmis: number;
                netLoanAmount: number;
                customerROI: number;
                totalProcessingFee: number;
                monthlyEMI: number;
                totalInterest: number;
                totalDownPayment: number;
                advancedEmiPayment: number;
                DBDToDealer: number;
                DealerGets: number;
                netDisbursement: number;
                name: string;
                emiStartDate?: string;
            };
        };

        settlement?: {
            customerName?: string;
            customerMobileNo?: string;
            orderId?: string;
            schemeName?: string;
            schemeTenure?: number;
            schemeEMIAmount?: number;
            totalAmount?: number;
            productName?: string;
            productCategory?: string;
            productBrand?: string;
            schemeDownPayment?: number;
            paymentFrequency?: string;
            netDisbursement?: number;
            interestAmount?: number;
            emiStartDate?: string;
        };
    };
}

export const getCurrentOrder =
    async (): Promise<CurrentOrderResponse> => {

        const token =
            localStorage.getItem("journey_token");

        if (!token) {
            throw new ApiError(
                "Journey session expired.",
                401
            );
        }

        const response =
            await fetch(
                `${API_BASE_URL}/order/details`,
                {
                    method: "GET",

                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "application/json",
                    },
                }
            );

        const data =
            await response.json();

        if (!response.ok) {

            throw new ApiError(
                data?.message ||
                data?.data?.message ||
                "Unable to fetch order details.",
                response.status,
                data
            );

        }

        return data;
    };

/*
 * =========================================================
 * GET ORDER KFS
 * =========================================================
 *
 * GET /order/:orderId/kfs
 */

export interface OrderKfsResponse {
    success: boolean;

    data: {
        orderId: string;

        amount: number;

        lender?: string;

        productDetails?: string;
        productCategory?: string;
        productBrand?: string;

        keyFactStatement?: {
            amount: number;

            scheme?: {
                _id: string;
                name: string;
                grossTenure: number;
                advancedEMI: number;
                subventionCost: number;
                interestBased: boolean;
                annualInterest: number;
                processingRate: number;
                GST: number;
                schemeId: string;
            };

            calculations?: {
                loanAmount: number;
                grossTenure: number;
                advancedEMI: number;
                NoOfNetEmis: number;
                netLoanAmount: number;
                customerROI: number;
                totalProcessingFee: number;
                monthlyEMI: number;
                totalInterest: number;
                totalDownPayment: number;
                advancedEmiPayment: number;
                DBDToDealer: number;
                DealerGets: number;
                netDisbursement: number;
                name: string;
            };
        };

        settlement?: {
            customerName?: string;
            customerMobileNo?: string;
            orderId?: string;
            schemeName?: string;
            schemeTenure?: number;
            schemeEMIAmount?: number;
            totalAmount?: number;
            productName?: string;
            productCategory?: string;
            productBrand?: string;
            partnerName?: string;
            partnerMobileNo?: string;
            schemeDownPayment?: number;
            paymentFrequency?: string;
            netDisbursement?: number;
            interestAmount?: number;
        };
    };
}

export const getOrderKfs = async (
    orderId: string
): Promise<OrderKfsResponse> => {

    if (!orderId) {
        throw new ApiError(
            "Order ID is required.",
            400
        );
    }

    const response = await fetch(
        `${API_BASE_URL}/order/${encodeURIComponent(
            orderId
        )}/kfs`,
        {
            method: "GET",

            headers: {
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(
            data?.message ||
            data?.data?.message ||
            "Unable to fetch Key Fact Statement.",
            response.status,
            data
        );
    }

    return data;
};

/**
 * =========================================================
 * REJECT PRE-APPROVAL
 * =========================================================
 *
 * JWT contains journeyId.
 *
 * Backend:
 *   JWT → journeyId
 *       → Journey.status = REJECTED
 *       → PRE_APPROVAL rejected webhook
 *
 * Used when customer clicks Back on the
 * pre-approval rejected screen.
 * =========================================================
 */

export interface RejectPreApprovalResponse {
    success: boolean;
    message: string;
    data?: {
        journey?: JourneyData;
    };
}

export const rejectPreApproval =
    async (): Promise<RejectPreApprovalResponse> => {

        const token =
            localStorage.getItem("journey_token");

        if (!token) {
            throw new ApiError(
                "Journey session expired.",
                401
            );
        }

        const response = await fetch(
            `${API_BASE_URL}/journey/pre-approval/reject`,
            {
                method: "POST",

                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new ApiError(
                data?.message ||
                data?.data?.message ||
                "Unable to update pre-approval status.",
                response.status,
                data
            );
        }

        return data;
    };
